import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Users,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Card } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { dashboardService } from '../../lib/database/dashboard';
import { invoiceService } from '../../lib/database/invoices';

interface DashboardStats {
  totalRevenue: number;
  totalOutstanding: number;
  totalExpenses: number;
  invoiceCount: number;
  paidInvoices: number;
  overdueInvoices: number;
}

const mockRevenueData = [
  { month: 'Jan', income: 35000, expenses: 12000 },
  { month: 'Feb', income: 42000, expenses: 15000 },
  { month: 'Mar', income: 38000, expenses: 11000 },
  { month: 'Apr', income: 49000, expenses: 18000 },
  { month: 'May', income: 52000, expenses: 16000 },
  { month: 'Jun', income: 48000, expenses: 14000 },
];

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    totalOutstanding: 0,
    totalExpenses: 0,
    invoiceCount: 0,
    paidInvoices: 0,
    overdueInvoices: 0,
  });
  const [recentInvoices, setRecentInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch dashboard stats
        const { data: dashboardStats } = await dashboardService.getDashboardStats();
        if (dashboardStats) {
          setStats(dashboardStats);
        }

        // Fetch recent invoices
        const { data: invoices } = await invoiceService.getInvoices();
        if (invoices) {
          setRecentInvoices(invoices.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const revenueGrowth = 22; // Mock growth percentage

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Monitor your business performance and key metrics</p>
      </div>

      {/* AI Insight Banner */}
      <Card className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-2">AI Business Insight</h3>
            <p className="text-primary-100">
              {stats.invoiceCount === 0 
                ? "Get started by adding your first client and creating invoices to track your business growth."
                : `You have ${stats.invoiceCount} invoices with ${stats.paidInvoices} paid. Consider following up on pending payments.`
              }
            </p>
          </div>
          <TrendingUp className="w-12 h-12 text-primary-200" />
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card hover className="text-center">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
            {stats.totalRevenue > 0 && (
              <div className="flex items-center text-success-600">
                <ArrowUpRight className="w-4 h-4" />
                <span className="text-sm font-medium">+{revenueGrowth}%</span>
              </div>
            )}
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</h3>
          <p className="text-gray-600">Total Revenue</p>
        </Card>

        <Card hover className="text-center">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-warning-600" />
            </div>
            {stats.totalOutstanding > 0 && (
              <div className="flex items-center text-warning-600">
                <ArrowDownRight className="w-4 h-4" />
                <span className="text-sm font-medium">-8%</span>
              </div>
            )}
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalOutstanding)}</h3>
          <p className="text-gray-600">Outstanding</p>
        </Card>

        <Card hover className="text-center">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-error-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-error-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalExpenses)}</h3>
          <p className="text-gray-600">Total Expenses</p>
        </Card>

        <Card hover className="text-center">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{stats.invoiceCount}</h3>
          <p className="text-gray-600">Total Invoices</p>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Income vs Expenses</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockRevenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip
                formatter={(value: number) => [formatCurrency(value), '']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Bar dataKey="income" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mockRevenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip
                formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                labelStyle={{ color: '#374151' }}
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
              />
              <Line 
                type="monotone" 
                dataKey="income" 
                stroke="#14b8a6" 
                strokeWidth={3}
                dot={{ fill: '#14b8a6', strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Recent Invoices</h3>
          <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            View All
          </button>
        </div>
        <div className="space-y-4">
          {recentInvoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No invoices yet. Create your first invoice to get started!</p>
            </div>
          ) : (
            recentInvoices.map((invoice) => (
              <div key={invoice.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                      <p className="text-sm text-gray-600">{invoice.client?.name || 'Unknown Client'}</p>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{formatCurrency(Number(invoice.total))}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    invoice.status === 'paid' 
                      ? 'bg-success-100 text-success-700'
                      : invoice.status === 'overdue'
                      ? 'bg-error-100 text-error-700'
                      : 'bg-warning-100 text-warning-700'
                  }`}>
                    {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
};